import { LngLatBounds, LngLat } from 'maplibre-gl'

export const GAME_BASIS = 100  // Places are 0.01 degrees wide and tall

export type Coordinates = [LngLat, LngLat, LngLat, LngLat];

export type EvmAddress = string; // Ethereum Virtual Machine

export type Player = {
    address: EvmAddress;
    ens?: string;
    isMe?: boolean;
    slapCount?: number;
    stickerCount?: number;
}

export type PlayerLookupTable = {
    [address: EvmAddress]: Player | null;
}


export type OnchainPaymentMethod = {
    total: bigint;
    paymentMethodId: bigint;
}

export type Board = {
    placeStore: PlaceGroup;
    slapStore: SlapLookupTable;
    stickerDesignStore: StickerDesignLookupTable;
    objectiveStore: ObjectiveGroup;
    paymentMethodStore: PaymentMethodGroup;
    playerStore: PlayerLookupTable;
    miniAppDialogContainer?: HTMLElement | null;
}

export const emptyBoard = {
    placeStore: {},
    slapStore: {},
    stickerDesignStore: {},
    objectiveStore: {},
    paymentMethodStore: {},
    playerStore: {},
} as Board

export const emptyBounds : LngLatBounds = new LngLatBounds(new LngLat(0,0), new LngLat(0,0))

export const emptyCoordinates : Coordinates  = [
    new LngLat(0,0),
    new LngLat(0,0),
    new LngLat(0,0),
    new LngLat(0,0),
]

export type SelectedItem = {
    type: 'none' | 'place' | 'objective';
    id?: PlaceId | ObjectiveId;
    bounds?: LngLatBounds;
    place?: Place;
    objective?: Objective;
    expandedSlap?: ExpandedSlap;
}

export type FocusedPlace = {
    placeId: PlaceId | null;
    source?: 'map' | 'sidebar' | 'activity' | 'url';
}

export enum SidebarMode {
    Latest = 'Recent',
    Objectives = 'Objectives',
    Place = 'Place History',
}

export const emptyDisplayedCenter = {
    center: new LngLat(0,0),
    zoom: 1,
    placeId: 0,
    bounds: emptyBounds,
}

export const defaultDisplayedCenter = {
    center: new LngLat(-74, 40.73),
    zoom: 12,
    placeId: 7113672999,
    bounds: new LngLatBounds(new LngLat(-74.06, 40.67), new LngLat(-73.93, 40.79)),
}

export type DisplayedCenter = {
    center: LngLat;
    zoom: number;
    placeId: PlaceId;
    bounds: LngLatBounds;
}

export type PlaceId = number;

export type Place = {
    id: PlaceId; // every place on the board has an id which encodes its latitude and longitude
    bounds?: LngLatBounds; // bounds of Place
    slap?: SlapId; // id of current slap in place
    slapTime?: number; // timestamp of current slap in place
    pendingSlaps?: SlapId[]; // id of slaps in progress in place by player
}

export type PlaceGroup = {
    [placeId: PlaceId]: Place | null;
}

export type ObjectiveId = number;

export type Objective = {
    id: ObjectiveId;
    address?: EvmAddress;
    name: string;
    docPath?: string;
    slaps: SlapId[];
    description?: string;
    priceDesc?: string;
    bounds?: LngLatBounds;
    places?: PlaceGroup;
}

export type ObjectiveGroup = {
    [objectiveId: ObjectiveId]: Objective | null;
}

export type ApiObjectiveSlapResponse = {
    id: ObjectiveId;
    name: string;
    playerAddress: EvmAddress;
    playerEns: string;
    slappedAt: string;
    slapId: string;
    placeId: string;
    height: string;
    stickerDesignId: string;
    size: string;
    ownerAddress: EvmAddress;
    blockNumber: string;
    contentType: string;
    aspectRatio: string;
    stickerSize: string;
    imageCID: string;
    objectiveIds: string[];
}

export type PaymentMethodId = number;
export type PaymentMethod = {
    id: PaymentMethodId;
    address: EvmAddress;
    name: string;
    symbol: string;
    decimals: number;
}

export type PaymentMethodGroup = {
    [paymentMethodId: PaymentMethodId]: PaymentMethod | null;
}

export type StickerDesignId = number;

export type StickerDesign = {
    id: StickerDesignId;
    name: string;
    originalPublisher: EvmAddress;
    originalPublisherEns?: string;
    currentPublisher: EvmAddress;
    currentPublisherEns?: string;
    payoutAddress: EvmAddress;
    publishedAt: number;
    paymentMethodId: PaymentMethodId;
    price: number;
    limit: number | null;
    endTime: number | null;
    limitToHolders?: string | null;
    aspectRatio: number; // float between 0.5 and 2 with 2 digit precision
    imageCID?: string; // IPFS CID v1
    metaCID?: string; // IPFS CID v1
    filename?: string;
    collectionId?: number;
    collectionItemId?: number; // id of token in remote collections
    url?: string;
    description?: string;
    slapCount?: number;
}

export type ApiResponseStickerDesign = {
    id: StickerDesignId;
    slapCount: string;
    vid: string;
    stickerId: string;
    active: boolean;
    banned: boolean;
    subgraphEntityId: string | null;
    originalPublisher: EvmAddress;
    currentPublisher: EvmAddress;
    payoutAddress: EvmAddress;
    publishedAt: string;
    price: string;
    paymentMethodId: string;
    limitToHolders: string | null;
    limit: string | null;
    endTime: string | null;
    metadataCID: string;
    metadataLoaded: boolean;
    name: string;
    imageCID: string;
    filename: string;
    contentType: string;
    aspectRatio: string;
    size: string;
    url: string;
    description: string;
    originalPublisherEns: string | null;
    currentPublisherEns: string | null;
}

export type StickerMetadata = {
    name: string;
    publisher: EvmAddress;
    imageCID: string;
    size: number;
    contentType: string;
    aspectRatio: number;
    filename: string;
    url?: string;
    description?: string;
}

export type NewSlap = {
    placeId: PlaceId;
    stickerId: StickerDesignId;
    size: number;
}

export type NewStickerDesign = {
    payoutAddress: `0x${string}`;
    price: number;
    limitCount: number;
    limitTime: number;
    metadataCID: `0x${string}`;
}

export const StickerImageMaxSizeInBytes = 819200


export type StickerDesignLookupTable = {
    [id: number]: StickerDesign | null;
};

export type PublishedStickerData = {
    stickerId: StickerDesignId;
    name: string;
    createdAt: string;
    imageCID: string;
    isPending?: boolean;
    metadataCID?: string;
}

export type SlapId = number;

export type Slap = {
    id: SlapId;
    time: number; // timestamp
    size: number; // width of slap in places
    place: number; // location of slap given as place id
    sticker: number; // StickerDesign id
    player: string; // account address of player who slapped
    playerEns?: string; // ens name of player who slapped
    isDraft?: boolean; // true if slap is not yet confirmed
    isSubmitted?: boolean; // true if slap has been submitted to blockchain
    objectiveIds?: ObjectiveId[]; // ids of objectives this slap belongs to
    imageCID?: string; // IPFS CID for the sticker image
    aspectRatio?: number; // aspect ratio of the sticker
    originalTimestamp?: string; // original ISO timestamp string for API calls
}

// ExpandedSlap is similar to Slap but it holds full
// structures for Place and Sticker instead of just ids
export type ExpandedSlap = {
    id: SlapId;
    time?: number;
    size?: number;
    place: Place;
    sticker: StickerDesign;
    player: string;
    playerEns?: string;
    isDraft?: boolean;
    isSubmitted?: boolean;
    objectiveIds?: ObjectiveId[];
}

export function convertExpandedSlapToSlap(expandedSlap: ExpandedSlap): Slap {
    return {
        id: expandedSlap.id,
        time: expandedSlap.time || 0,
        size: expandedSlap.size || 1,
        place: expandedSlap.place.id,
        sticker: expandedSlap.sticker.id,
        player: expandedSlap.player,
        isDraft: expandedSlap.isDraft,
        isSubmitted: expandedSlap.isSubmitted,
        objectiveIds: expandedSlap.objectiveIds,
    };
}


export type NewSlapOnchain = {
    placeId: bigint;
    stickerId: bigint;
    size: bigint;
}

export type SlapLookupTable = {
    [slap: SlapId]: Slap | null;
}

export type PendingSlapList = ExpandedSlap[];

export type ActivityType = 'publish' | 'slap';

export type BaseActivity = {
    id: string; // UUID or timestamp-based unique ID
    timestamp: number;
    type: ActivityType;
}

export type PublishActivity = BaseActivity & {
    type: 'publish';
    stickerId: StickerDesignId;
    stickerName: string;
    imageCID: string;
    metadataCID?: string;
}

export type SlapActivity = BaseActivity & {
    type: 'slap';
    slapId: SlapId;
    placeId: PlaceId;
    stickerId: StickerDesignId;
    size: number;
    imageCID: string;
}

export type Activity = PublishActivity | SlapActivity;

export type ActivityList = Activity[];
